import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronDown, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { companyInfo, stats } from '../data/mock';
import { heroImages } from '../data/images';
import { trackCTAClick } from '../utils/analytics';

const Hero = () => {
  const navigate = useNavigate();
  const [currentImage, setCurrentImage] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImage(prev => (prev + 1) % heroImages.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const scrollToConsultation = () => {
    trackCTAClick('Schedule a Strategy Session', 'hero');
    document.querySelector('#consultation')?.scrollIntoView({ behavior: 'smooth' });
  };

  const goToAssessment = () => {
    trackCTAClick('Get Your Free Alignment Score', 'hero');
    navigate('/assessment');
  };

  const scrollToChallenges = () => {
    document.querySelector('#challenges')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
      {/* Rotating Background Images */}
      {heroImages.map((img, i) => (
        <div
          key={img}
          className="absolute inset-0 transition-opacity duration-2000"
          style={{ opacity: i === currentImage ? 1 : 0, transitionDuration: '2000ms' }}
        >
          <img
            src={img}
            alt=""
            className="w-full h-full object-cover"
            loading={i === 0 ? 'eager' : 'lazy'}
          />
        </div>
      ))}

      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-950/85 to-slate-950/70" />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-slate-950/40" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 sm:pt-32 pb-16 sm:pb-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm mb-6 sm:mb-8">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-slate-300 text-sm font-medium">Leadership &bull; Workforce &bull; AI Solutions</span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[3.25rem] font-bold text-white mb-6 leading-[1.15]">
              Build Stronger Leaders. Aligned Teams.
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-violet-400 to-emerald-400">
                Real Results.
              </span>
            </h1>

            <p className="text-base sm:text-lg text-slate-300 mb-8 leading-relaxed max-w-xl">
              Trusted by organizations to develop leadership, strengthen teams, and improve performance through training, coaching, and AI-driven solutions.
            </p>

            {/* Dual CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <Button
                onClick={goToAssessment}
                data-testid="hero-assessment-cta"
                size="lg"
                className="w-fit bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-500 hover:to-cyan-500 text-white font-semibold px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg transition-all duration-300 hover:shadow-xl hover:shadow-violet-500/20"
              >
                Get Your Free Alignment Score
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                onClick={scrollToConsultation}
                data-testid="hero-cta-button"
                size="lg"
                variant="outline"
                className="w-fit border-white/20 text-white hover:bg-white/10 font-semibold px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg backdrop-blur-sm"
              >
                Schedule a Strategy Session
              </Button>
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-slate-300 text-sm mb-8">
              <span className="flex items-center gap-1.5">
                <CheckCircle className="h-4 w-4 text-emerald-400" />
                No cost consultation
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle className="h-4 w-4 text-emerald-400" />
                Customized to your organization
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle className="h-4 w-4 text-emerald-400" />
                Workforce funding may be available
              </span>
            </div>

            <p className="text-slate-400 text-sm">
              Trusted by UNLV, Clark County School District, City of Las Vegas, and more.
            </p>
          </div>

          {/* Right Stats Grid */}
          <div className="hidden lg:block">
            <div className="grid grid-cols-2 gap-4">
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="group p-6 rounded-2xl bg-white/[0.06] border border-white/10 backdrop-blur-md hover:bg-white/[0.1] hover:border-white/20 transition-all duration-300"
                >
                  <div className="text-3xl md:text-4xl font-bold text-white mb-2 group-hover:text-cyan-400 transition-colors">
                    {stat.value}
                  </div>
                  <div className="text-slate-300 text-sm font-medium">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 backdrop-blur-sm">
              <p className="text-emerald-300 text-sm text-center font-medium">
                Eligible organizations may qualify for workforce training funding or reimbursement.
              </p>
            </div>
          </div>
        </div>

        {/* Mobile Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-12 lg:hidden">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center p-4 rounded-xl bg-white/[0.06] border border-white/10 backdrop-blur-sm">
              <div className="text-xl sm:text-2xl font-bold text-white mb-1">{stat.value}</div>
              <div className="text-slate-300 text-xs font-medium">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden sm:flex flex-col items-center gap-2">
          <button onClick={scrollToChallenges} className="flex flex-col items-center gap-2 text-slate-400 hover:text-white transition-colors">
            <span className="text-xs uppercase tracking-widest">Explore</span>
            <ChevronDown className="h-5 w-5 animate-bounce" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Hero;
